import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Modal, ModalBody } from "reactstrap";
import { withTranslation } from "react-i18next";
import Text from "components/Text";
import Input from "components/Input";
import Loader from "components/Loader";
import Empty from "components/Empty";
import CustomDropdown from "components/Dropdown";
import crossIcon from "assets/images/cross.svg";
import plusIcon from "assets/images/plus-icon-outline.svg";
import searchIcon from "assets/images/search-icon.svg";
import closeIcon from "assets/images/close-icon.svg";
import sortIcon from "assets/images/mir-ico-sort.svg";
import ThreeDots from "assets/images/threedots.svg";
import infoIcon from "assets/images/info_black-tooltip.svg";
import { useSelectCredentialName } from "features/credentials/hooks/useSelectCredentialName";
import {
  CREDENTIAL_NAME_MAX_LENGTH,
  CREDENTIAL_NAME_SORT,
  getCredentialTypeLabel,
  getCredentialTypeOptions,
} from "features/credentials/credentialTypes";
import styles from "./SelectCredentialNameModal.module.scss";

/**
 * Scrolls a table row into the list container without moving outer scrollers.
 * @param {HTMLElement} container
 * @param {HTMLElement} element
 */
function scrollRowIntoList(container, element) {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  if (elementRect.bottom > containerRect.bottom) {
    container.scrollTop += elementRect.bottom - containerRect.bottom;
  } else if (elementRect.top < containerRect.top) {
    container.scrollTop -= containerRect.top - elementRect.top;
  }
}

/**
 * Fixed-position Edit/Delete menu so it is not clipped by the table or hidden
 * behind the parent credential modal.
 */
function CredentialNameActionsMenu({
  item,
  isOpen,
  onToggle,
  onEdit,
  onDelete,
  t,
}) {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, right: 0 });

  const updatePosition = () => {
    if (!buttonRef.current) {
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    setCoords({
      top: rect.bottom + 4,
      right: Math.max(window.innerWidth - rect.right, 8),
    });
  };

  const handleToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isOpen) {
      updatePosition();
    }
    onToggle(isOpen ? null : item.id);
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    updatePosition();
    const handleClickAway = (event) => {
      if (
        menuRef.current?.contains(event.target) ||
        buttonRef.current?.contains(event.target)
      ) {
        return;
      }
      onToggle(null);
    };
    const handleReposition = () => onToggle(null);
    document.addEventListener("mousedown", handleClickAway);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      document.removeEventListener("mousedown", handleClickAway);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen, onToggle]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`ellipsis-icon ${styles["ellipsis-btn"]}${
          isOpen ? ` ${styles["is-open"]}` : ""
        }`}
        onClick={handleToggle}
        aria-label={t("actions")}
      >
        <img src={ThreeDots} alt="ThreeDots" />
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className={styles["actions-menu"]}
            style={{ top: coords.top, right: coords.right }}
          >
            <button
              type="button"
              className={styles["actions-menu-item"]}
              onClick={() => {
                onToggle(null);
                onEdit(item);
              }}
            >
              {t("edit")}
            </button>
            <button
              type="button"
              className={`${styles["actions-menu-item"]}${
                item.isDeletable ? "" : ` ${styles["disabled-delete"]}`
              }`}
              aria-disabled={!item.isDeletable}
              onClick={() => {
                if (!item.isDeletable) {
                  return;
                }
                onToggle(null);
                onDelete(item);
              }}
            >
              <span>{t("delete")}</span>
              {!item.isDeletable && (
                <span
                  className={styles["delete-info-wrap"]}
                  onClick={(event) => event.stopPropagation()}
                >
                  <img
                    src={infoIcon}
                    alt=""
                    className={styles["delete-info"]}
                  />
                  <span
                    className={styles["delete-tooltip-text"]}
                    role="tooltip"
                  >
                    {t("accountOwner.cannotDeleteCredentialNameInUse")}
                  </span>
                </span>
              )}
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * Picker for Account Owner / Admin credential names, with search, sort,
 * and optional add/edit/delete for users who can manage the master list.
 */
function SelectCredentialNameModal({
  t,
  isOpen,
  closeModal,
  ownerId,
  selectedCredential,
  onSelect,
  canManageNames,
  showStaffInfoMessage,
  onCredentialUpdated,
  onCredentialDeleted,
  isFromProfile = false,
}) {
  const {
    selectedVal,
    searchInput,
    setSearchInput,
    sortBy,
    isDescending,
    handleSort,
    filteredList,
    isLoading,
    isFormOpen,
    formName,
    setFormName,
    formType,
    setFormType,
    formErrors,
    setFormErrors,
    selectError,
    openAddForm,
    openEditForm,
    closeForm,
    handleSaveForm,
    credentialToDelete,
    setCredentialToDelete,
    handleConfirmDelete,
    handleSelectCredential,
    handleApply,
    scrollToId,
    clearScrollToId,
  } = useSelectCredentialName({
    ownerId,
    isOpen,
    selectedCredential,
    t,
    onCredentialUpdated,
    onCredentialDeleted,
    isFromProfile,
  });

  const typeOptions = getCredentialTypeOptions(t);
  const [openActionId, setOpenActionId] = useState(null);
  const tableWrapRef = useRef(null);

  useEffect(() => {
    if (!scrollToId || isLoading || !tableWrapRef.current) {
      return undefined;
    }
    const frameId = requestAnimationFrame(() => {
      const container = tableWrapRef.current;
      if (!container) {
        return;
      }
      const row = container.querySelector(
        `[data-credential-id="${CSS.escape(String(scrollToId))}"]`,
      );
      if (!row) {
        return;
      }
      scrollRowIntoList(container, row);
      clearScrollToId();
    });
    return () => cancelAnimationFrame(frameId);
  }, [scrollToId, filteredList, isLoading, clearScrollToId]);

  const handleClose = () => {
    closeForm();
    setOpenActionId(null);
    closeModal();
  };

  const handleSelect = () => {
    const selected = handleApply();
    if (!selected) {
      return;
    }
    onSelect(selected);
    closeModal();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={handleClose}
        className="modal-dialog-centered modal-width-660"
        modalClassName="custom-modal"
        zIndex={2000}
      >
        <span className="close-btn" onClick={handleClose}>
          <img src={crossIcon} alt="close" />
        </span>
        <ModalBody>
          {isLoading && <Loader />}
          <Text size="25px" marginBottom="16px" weight="500" color="#111b45">
            <span className="modal-title-25">
              {t("accountOwner.selectCredentialName")}
            </span>
          </Text>

          {canManageNames && !isFormOpen && (
            <button
              type="button"
              className={styles["add-link"]}
              onClick={openAddForm}
            >
              <img src={plusIcon} alt="" />
              {t("accountOwner.addNewCredential")}
            </button>
          )}

          {showStaffInfoMessage && !canManageNames && (
            <div className={styles["info-banner"]}>
              <span>{t("accountOwner.credentialNameStaffInfoMessage")}</span>
            </div>
          )}

          {canManageNames && isFormOpen && (
            <div className={styles["add-form"]}>
              <div className={styles["add-form-fields"]}>
                <Input
                  Autofocus
                  Title={t("accountOwner.credentialName")}
                  Type="text"
                  Placeholder={t("form.placeholder1", { field: "" })}
                  MaxLength={CREDENTIAL_NAME_MAX_LENGTH}
                  Name="credentialMasterName"
                  Value={formName}
                  HandleChange={(e) => {
                    setFormName(e.target.value);
                    if (formErrors.name) {
                      setFormErrors((prev) => ({ ...prev, name: "" }));
                    }
                  }}
                  Error={formErrors.name}
                />
                <div className={`c-field${formErrors.type ? " error-input" : ""}`}>
                  <label>{t("accountOwner.type")}</label>
                  <div className="custom-dropdown-only">
                    <CustomDropdown
                      options={typeOptions}
                      selectedOption={formType}
                      selectOption={(id) => {
                        setFormType(id);
                        if (formErrors.type) {
                          setFormErrors((prev) => ({ ...prev, type: "" }));
                        }
                      }}
                      defaultValue={t("accountOwner.selectType")}
                    />
                  </div>
                  {formErrors.type && (
                    <span className="error-msg">{formErrors.type}</span>
                  )}
                </div>
              </div>
              <div className={styles["add-form-actions"]}>
                <button
                  type="button"
                  className="button button-round button-shadow"
                  onClick={handleSaveForm}
                >
                  {t("add")}
                </button>
                <button
                  type="button"
                  className="button button-round button-border button-dark"
                  onClick={closeForm}
                >
                  {t("cancel")}
                </button>
              </div>
            </div>
          )}

          <div className={styles["search-bar"]}>
            <img src={searchIcon} alt="" className={styles["search-icon"]} />
            <input
              type="text"
              placeholder={t("accountOwner.search")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput.trim() && (
              <button
                type="button"
                className={styles["clear-btn"]}
                onClick={() => setSearchInput("")}
                aria-label={t("accountOwner.clearSearch")}
              >
                <img src={closeIcon} alt="" />
              </button>
            )}
          </div>

          {filteredList.length > 0 ? (
            <div className={styles["table-wrap"]} ref={tableWrapRef}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>
                      <button
                        type="button"
                        className={styles["sort-btn"]}
                        onClick={() => handleSort(CREDENTIAL_NAME_SORT.NAME)}
                      >
                        <span>{t("accountOwner.credentialName")}</span>
                        <img
                          src={sortIcon}
                          alt=""
                          style={{
                            transform:
                              isDescending &&
                              sortBy === CREDENTIAL_NAME_SORT.NAME
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                          }}
                        />
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={styles["sort-btn"]}
                        onClick={() => handleSort(CREDENTIAL_NAME_SORT.TYPE)}
                      >
                        <span>{t("accountOwner.type")}</span>
                        <img
                          src={sortIcon}
                          alt=""
                          style={{
                            transform:
                              isDescending &&
                              sortBy === CREDENTIAL_NAME_SORT.TYPE
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                          }}
                        />
                      </button>
                    </th>
                    {canManageNames && (
                      <th className={styles["actions-cell"]}>{t("actions")}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredList.map((item) => {
                    return (
                      <tr key={item.id} data-credential-id={item.id}>
                        <td className={styles["name-cell"]}>
                          <div className="ch-radio">
                            <label>
                              <input
                                type="radio"
                                name="selectedCredentialName"
                                checked={
                                  String(selectedVal?.id) === String(item.id)
                                }
                                onChange={() => handleSelectCredential(item)}
                              />
                              <span>{item.credentialName}</span>
                            </label>
                          </div>
                        </td>
                        <td
                          className={styles["type-cell"]}
                          onClick={() => handleSelectCredential(item)}
                        >
                          {getCredentialTypeLabel(item.type, t)}
                        </td>
                        {canManageNames && (
                          <td className={styles["actions-cell"]}>
                            <CredentialNameActionsMenu
                              item={item}
                              isOpen={String(openActionId) === String(item.id)}
                              onToggle={setOpenActionId}
                              onEdit={openEditForm}
                              onDelete={setCredentialToDelete}
                              t={t}
                            />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            !isLoading && (
              <div className={styles["empty-state"]}>
                <Empty
                  Message={t("accountOwner.noCredentialsAvailableForSelection")}
                />
              </div>
            )
          )}

          {selectError && <span className="error-msg">{selectError}</span>}

          <button
            className="button button-round button-shadow mr-md-4 mb-3 w-sm-100"
            title={t("accountOwner.select")}
            type="button"
            onClick={handleSelect}
          >
            {t("accountOwner.select")}
          </button>
          <button
            className="button button-round button-border button-dark btn-mobile-link"
            onClick={handleClose}
            title={t("cancel")}
            type="button"
          >
            {t("cancel")}
          </button>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={!!credentialToDelete}
        toggle={() => setCredentialToDelete(null)}
        className="modal-dialog-centered profile-specialty-modal"
        modalClassName="custom-modal"
        zIndex={2100}
      >
        <span className="close-btn" onClick={() => setCredentialToDelete(null)}>
          <img src={crossIcon} alt="close" />
        </span>
        <ModalBody>
          <Text
            size="25px"
            marginBottom="11px"
            weight="500"
            color="#111b45"
            className="text-center"
          >
            <span className="modal-title-25">
              {t("accountOwner.deleteCredentialTitle")}
            </span>
          </Text>
          <div className="text-center mb-4">
            {t("accountOwner.deleteCredentialConfirmation")}
          </div>
          <div className="d-md-flex flex-row btn-box justify-content-center">
            <button
              className="button button-round button-shadow mr-md-4 mb-3 w-sm-100"
              type="button"
              onClick={handleConfirmDelete}
            >
              {t("delete")}
            </button>
            <button
              className="button mb-md-3 button-round button-border button-dark btn-mobile-link"
              type="button"
              onClick={() => setCredentialToDelete(null)}
            >
              {t("cancel")}
            </button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}

export default withTranslation()(SelectCredentialNameModal);
